import { EllipsisMenu, Menu } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { setActiveRenameInput } from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import css from './ArtboardTabs.module.scss'

type Props = {
  id: string
  activeTab: string | null
  isOneItem: boolean
  isFirstElement: boolean
  isLastElement: boolean
  disabledButton: boolean
  disabledCopyButton: boolean
  isReadOnly: boolean
  onSetActive: (id: string) => void
  onDelete: (id: string, name: string) => void
  onDuplicate: (id: string) => void
  onMoveUp: (id: string) => void
  onMoveDown: (id: string) => void
  onRenameTab: (id: string, name: string) => void
  onCopyLink: (id: string) => void
}

export const ArtboardTabsItem: React.FC<Props> = ({
  id,
  activeTab,
  isOneItem,
  isFirstElement,
  isLastElement,
  disabledButton,
  disabledCopyButton,
  isReadOnly,
  onSetActive,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onRenameTab,
  onCopyLink,
}) => {
  const dispatch = useDispatch()

  const name = useSelector(migrationProjectSelector.getProjectTabTitle(id))

  const [isRename, setIsRename] = useState(false)
  const [inputValue, setInputValue] = useState(name)
  const [inputWidth, setInputWidth] = useState(name.length)
  const inputRef = useRef<HTMLInputElement>(null)
  const spanRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    setInputValue(name)
  }, [name])

  useEffect(() => {
    if (spanRef.current) {
      setInputWidth(spanRef.current.offsetWidth)
    }
  }, [inputValue])

  useEffect(() => {
    if (isRename && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRename])

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    closeDropdown: () => void,
    type: 'moveUp' | 'moveDown' | 'delete' | 'duplicate' | 'copyLink'
  ) => {
    event.stopPropagation()

    switch (type) {
      case 'moveUp': {
        if (isFirstElement || isReadOnly) {
          return
        }
        onMoveUp(id)
        break
      }
      case 'moveDown': {
        if (isLastElement || isReadOnly) {
          return
        }
        onMoveDown(id)
        break
      }
      case 'delete': {
        if (isOneItem || isReadOnly) {
          return
        }
        onDelete(id, name)
        closeDropdown()
        break
      }
      case 'duplicate': {
        if (disabledButton || isReadOnly) {
          return
        }
        onDuplicate(id)
        closeDropdown()
        break
      }
      case 'copyLink': {
        if (disabledCopyButton) {
          return
        }
        onCopyLink(id)
        closeDropdown()
        break
      }
      default:
        break
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target

    setInputValue(value)
  }

  const handleOnRenameTab = () => {
    if (inputValue === name || inputValue === '') {
      onRenameTab(id, name)
    } else {
      onRenameTab(id, inputValue)
    }

    setIsRename(false)
    dispatch(setActiveRenameInput(false))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleOnRenameTab()
    }
  }

  return (
    <Menu
      key={id}
      active={id === activeTab}
      customClassName={css.tabsItem}
      onClick={() => {
        if (isRename) {
          return
        }
        onSetActive(id)
      }}
    >
      {isRename ? (
        <div className={css.inputContainer}>
          <input
            ref={inputRef}
            value={inputValue}
            className={css.input}
            maxLength={24}
            style={{ width: inputWidth + 2 }}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <span className={css.hide} ref={spanRef}>
            {inputValue}
          </span>
          <button className={css.inputBtn} onClick={handleOnRenameTab}>
            Ok
          </button>
        </div>
      ) : (
        <div className={css.tabsName}>{name}</div>
      )}

      {!isRename && (
        <EllipsisMenu
          customClassName={css.tabsMenu}
          dropdownContainerClassName={css.tabsDropdown}
          position="top-start"
        >
          {(closeDropdown) => (
            <>
              <Menu
                disabled={isFirstElement || isReadOnly}
                noHover={isFirstElement || isReadOnly}
                onClick={(e) => handleMenuItemClick(e, closeDropdown, 'moveUp')}
              >
                Move up
              </Menu>
              <Menu
                disabled={isLastElement || isReadOnly}
                noHover={isLastElement || isReadOnly}
                onClick={(e) =>
                  handleMenuItemClick(e, closeDropdown, 'moveDown')
                }
              >
                Move down
              </Menu>
              <div
                className={css.divider}
                onClick={(e) => e.stopPropagation()}
              />
              <Menu
                customClassName={css.deleteBtn}
                disabled={isOneItem || isReadOnly}
                noHover={isOneItem || isReadOnly}
                onClick={(e) => handleMenuItemClick(e, closeDropdown, 'delete')}
              >
                Delete
              </Menu>
              <Menu
                disabled={disabledButton || isReadOnly}
                noHover={disabledButton || isReadOnly}
                onClick={(e) =>
                  handleMenuItemClick(e, closeDropdown, 'duplicate')
                }
              >
                Duplicate
              </Menu>
              <Menu
                disabled={disabledCopyButton}
                onClick={(e) =>
                  handleMenuItemClick(e, closeDropdown, 'copyLink')
                }
              >
                Copy link
              </Menu>
              <Menu
                disabled={isReadOnly}
                noHover={isReadOnly}
                onClick={(e) => {
                  e.stopPropagation()

                  if (isReadOnly) {
                    return
                  }

                  setIsRename(true)
                  dispatch(setActiveRenameInput(false))
                  closeDropdown()
                }}
              >
                Rename
              </Menu>
            </>
          )}
        </EllipsisMenu>
      )}
    </Menu>
  )
}
